import { useMemo, useState } from 'react';

import {
  Button,
  ContainerColumnLeft,
  SearchForm,
  TableContainer,
  TableHeader,
  Typography,
} from '@occc-app/design-system';
import { useBulkSelect } from '@occc-app/hooks/useBulkSelect';
import { pluralize } from '@occc-app/lib/strings';

import { RetailerNetworkUserDetailsLocationGroupsTable } from './retailer-network-user-details-location-groups-table.component';

import { useGetRetailerNetworkLocationGroups } from '../../../../../data-access/queries';
import { useRetailerNetworkUserSelector } from '../../../../retailer-network-context/use-retailer-network-user.selector';

type RetailerNetworkUserDetailsLocationGroupsTableContainerProps = {
  retailerNetworkUuid: string;
  selectLocationGroupUuids: (locationGroupUuids: string[]) => void;
  onClose: () => void;
};

const RetailerNetworkUserDetailsLocationGroupsTableContainer = ({
  retailerNetworkUuid,
  selectLocationGroupUuids,
  onClose,
}: RetailerNetworkUserDetailsLocationGroupsTableContainerProps) => {
  const [search, setSearch] = useState<string | undefined>(undefined);
  const { isRetailerNetworkSiteAdmin, retailerNetworkUserOpenCommerceUserUuid } =
    useRetailerNetworkUserSelector();
  const openCommerceUserUuidEquals = isRetailerNetworkSiteAdmin
    ? retailerNetworkUserOpenCommerceUserUuid
    : undefined;

  const { loading, error, data, paginationProps } = useGetRetailerNetworkLocationGroups({
    filter: {
      search,
      retailerNetworkUuidEquals: retailerNetworkUuid,
      openCommerceUserUuidEquals,
    },
  });

  const { bulkSelectedItems, handleBulkSelectMultiple, handleBulkDeselectAll, handleItemSelect } =
    useBulkSelect();

  const onSubmit = () => {
    const newLocationGroupUuids = Array.from(bulkSelectedItems);
    selectLocationGroupUuids(newLocationGroupUuids);

    onClose();
  };

  const headerActions: React.ReactNode = useMemo(() => {
    return (
      <SearchForm
        onSubmit={({ search }) => {
          setSearch(search);
        }}
      />
    );
  }, [setSearch]);

  const bulkSelectedItemsCount = bulkSelectedItems.size;

  return (
    <ContainerColumnLeft>
      <TableContainer>
        <TableHeader
          disableTitleTypography
          actions={headerActions}
          title={
            <Typography variant='h6' whiteSpace='nowrap'>
              Choose Location Groups to Add User to
            </Typography>
          }
          bulkSelected={bulkSelectedItemsCount}
          bulkActions={
            <Button color='primary' variant='contained' onClick={onSubmit}>
              {`Add User to Location ${pluralize(bulkSelectedItemsCount, 'Group')}`}
            </Button>
          }
        />
        <RetailerNetworkUserDetailsLocationGroupsTable
          loading={loading}
          data={data}
          error={error}
          paginationProps={paginationProps}
          bulkSelectedItems={bulkSelectedItems}
          handleBulkSelectMultiple={handleBulkSelectMultiple}
          handleBulkDeselectAll={handleBulkDeselectAll}
          handleItemSelect={handleItemSelect}
        />
      </TableContainer>
    </ContainerColumnLeft>
  );
};

export default RetailerNetworkUserDetailsLocationGroupsTableContainer;
